import * as React from 'react';
import { usePermissions } from 'react-admin';
import { httpClient } from '../utils/dataProvider';
import { API_BASE_URL } from '../utils/config';
import { hasAccess } from '../utils/permissions';
import {
    Empty,
    MetricCard,
    PageHeader,
    PageShell,
    Row,
    RowMeta,
    RowText,
    RowTitle,
    Search,
    StatusPill,
    Surface,
    Toolbar,
    StatePanel,
} from '../components/ui';

const MODE_LABELS = {
    OFF: 'OFF',
    DETECT: 'DETECTION ONLY',
    ENFORCE: 'ENFORCING',
};

const MODE_TONES = {
    OFF: 'blue',
    DETECT: 'orange',
    ENFORCE: 'green',
};

const STATUS_FILTERS = ['ALL', 'PENDING', 'CONFIRMED_PHONE', 'FALSE_POSITIVE'];
const VALID_RESOLUTIONS = ['CONFIRMED_PHONE', 'FALSE_POSITIVE'];
const NOTE_MAX_LENGTH = 500;

export default function ContactModerationQueue() {
    const { permissions } = usePermissions();
    const adminRoles = (permissions && permissions.adminRoles) || [];

    const [reviews, setReviews] = React.useState([]);
    const [counts, setCounts] = React.useState({ pending: 0, confirmed: 0, falsePositive: 0, total: 0 });
    const [mode, setMode] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [search, setSearch] = React.useState('');
    const [statusFilter, setStatusFilter] = React.useState('PENDING');
    const [page, setPage] = React.useState(1);
    const [hasNextPage, setHasNextPage] = React.useState(false);

    // Per-row resolution state: { [reviewId]: { resolution, note } }
    const [resolutions, setResolutions] = React.useState({});
    const [resolving, setResolving] = React.useState({});
    // Per-row error state: { [reviewId]: string }
    const [resolveErrors, setResolveErrors] = React.useState({});

    // RBAC guard — render denial for unauthorized roles
    if (!hasAccess('contact-moderation', adminRoles)) {
        return (
            <PageShell>
                <StatePanel state="error">You do not have permission to view this page.</StatePanel>
            </PageShell>
        );
    }

    return <ContactModerationQueueInner
        reviews={reviews} setReviews={setReviews}
        counts={counts} setCounts={setCounts}
        mode={mode} setMode={setMode}
        loading={loading} setLoading={setLoading}
        error={error} setError={setError}
        search={search} setSearch={setSearch}
        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        page={page} setPage={setPage}
        hasNextPage={hasNextPage} setHasNextPage={setHasNextPage}
        resolutions={resolutions} setResolutions={setResolutions}
        resolving={resolving} setResolving={setResolving}
        resolveErrors={resolveErrors} setResolveErrors={setResolveErrors}
    />;
}

function ContactModerationQueueInner({
    reviews, setReviews,
    counts, setCounts,
    mode, setMode,
    loading, setLoading,
    error, setError,
    search, setSearch,
    statusFilter, setStatusFilter,
    page, setPage,
    hasNextPage, setHasNextPage,
    resolutions, setResolutions,
    resolving, setResolving,
    resolveErrors, setResolveErrors,
}) {
    const load = React.useCallback(() => {
        setLoading(true);
        setError(null);
        const statusParam = statusFilter === 'ALL' ? '' : `?status=${statusFilter}&page=${page}`;
        const listUrl = statusFilter === 'ALL'
            ? `${API_BASE_URL}/moderationReviews?page=${page}`
            : `${API_BASE_URL}/moderationReviews${statusParam}`;

        Promise.all([
            httpClient(`${API_BASE_URL}/moderationReviews/counts`).then(({ json }) => setCounts(json)),
            httpClient(`${API_BASE_URL}/moderationReviews/mode`).then(({ json }) => setMode(json.mode)),
            httpClient(listUrl).then(({ json }) => {
                setReviews(json.docs || []);
                setHasNextPage(!!json.hasNextPage);
            }),
        ]).catch(err => {
            setError(err.message || 'Failed to load moderation data');
        }).finally(() => setLoading(false));
    }, [statusFilter, page, setLoading, setError, setCounts, setMode, setReviews, setHasNextPage]);

    React.useEffect(load, [load]);

    const handleResolve = (reviewId) => {
        const { resolution, note } = resolutions[reviewId] || {};
        if (!resolution || !VALID_RESOLUTIONS.includes(resolution)) return;

        // Clear any previous error for this row
        setResolveErrors(prev => {
            const next = { ...prev };
            delete next[reviewId];
            return next;
        });

        setResolving(prev => ({ ...prev, [reviewId]: true }));
        const body = { resolution };
        if (note) body.note = note.trim().slice(0, NOTE_MAX_LENGTH);

        httpClient(`${API_BASE_URL}/moderationReviews/${reviewId}/resolve`, {
            method: 'PATCH',
            body: JSON.stringify(body),
        }).then(() => {
            setResolutions(prev => {
                const next = { ...prev };
                delete next[reviewId];
                return next;
            });
            load();
        }).catch(err => {
            // Show per-row error; preserve selections/note for retry
            setResolveErrors(prev => ({
                ...prev,
                [reviewId]: err.message || 'Resolution failed. Please try again.',
            }));
        }).finally(() => setResolving(prev => ({ ...prev, [reviewId]: false })));
    };

    // Filter by search — client-side only (current page scope)
    const filtered = React.useMemo(() => {
        if (!search) return reviews;
        const q = search.toLowerCase();
        return reviews.filter(r =>
            (r.maskedContent || '').toLowerCase().includes(q) ||
            (r.sender?.name || '').toLowerCase().includes(q) ||
            (Array.isArray(r.reasons) ? r.reasons : []).some(reason =>
                String(reason).toLowerCase().includes(q)
            )
        );
    }, [reviews, search]);

    const modeLabel = MODE_LABELS[mode] || mode || '…';
    const modeTone = MODE_TONES[mode] || 'blue';

    /** Safely format confidence — guard NaN / undefined */
    const fmtConfidence = (c) => {
        const n = Number(c);
        if (!Number.isFinite(n)) return '—';
        return `${Math.round(n * 100)}%`;
    };

    /** Safely format status for display */
    const fmtStatus = (s) => {
        if (!s || typeof s !== 'string') return 'UNKNOWN';
        return s.replace(/_/g, ' ');
    };

    /** Map status to StatusPill tone */
    const statusTone = (s) => {
        if (s === 'PENDING') return 'pending';
        if (s === 'CONFIRMED_PHONE') return 'banned';
        return 'active';
    };

    return (
        <PageShell>
            <PageHeader
                eyebrow="Trust & Safety"
                title="Contact Sharing Moderation"
                description="Review flagged contact-sharing attempts in chat messages."
            />

            {/* Metric cards */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
                <MetricCard tone={modeTone} label="Mode" value={modeLabel} />
                <MetricCard tone="orange" label="Pending" value={counts.pending} />
                <MetricCard tone="green" label="Confirmed" value={counts.confirmed} />
                <MetricCard tone="blue" label="False Positive" value={counts.falsePositive} />
            </div>

            {/* Main surface */}
            <Surface
                title="Flagged Messages"
                description={`${filtered.length} review${filtered.length !== 1 ? 's' : ''}`}
            >
                <Toolbar right={
                    <Search
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search this page…"
                    />
                }>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {STATUS_FILTERS.map(s => (
                            <button
                                key={s}
                                onClick={() => { setStatusFilter(s); setPage(1); }}
                                style={{
                                    padding: '6px 12px',
                                    borderRadius: 10,
                                    border: statusFilter === s ? '2px solid #7c3aed' : '1px solid #e7eaf0',
                                    background: statusFilter === s ? '#f0eaff' : '#fff',
                                    color: statusFilter === s ? '#4e299c' : '#566071',
                                    fontWeight: statusFilter === s ? 700 : 500,
                                    fontSize: 12,
                                    cursor: 'pointer',
                                }}
                            >
                                {s === 'ALL' ? 'All' : s.replace(/_/g, ' ')}
                            </button>
                        ))}
                    </div>
                </Toolbar>

                {/* Search scope notice */}
                {search && (
                    <div style={{ padding: '4px 16px', fontSize: 11, color: '#7f8999', fontStyle: 'italic' }}>
                        Filtering within current page only.
                    </div>
                )}

                {/* States */}
                {loading && <StatePanel state="loading">Loading flagged messages…</StatePanel>}
                {error && <StatePanel state="error">Error: {error}</StatePanel>}
                {!loading && !error && filtered.length === 0 && (
                    <Empty>No flagged messages match the current filter.</Empty>
                )}

                {/* Review rows */}
                {!loading && !error && filtered.map(r => (
                    <Row
                        key={r._id}
                        right={
                            <div style={{ textAlign: 'right' }}>
                                <StatusPill status={statusTone(r.status)}>
                                    {fmtStatus(r.status)}
                                </StatusPill>
                                <div style={{ fontSize: 11, color: '#7f8999', marginTop: 4 }}>
                                    {fmtConfidence(r.confidence)} confidence
                                </div>
                            </div>
                        }
                    >
                        <RowText>
                            <RowTitle>
                                {r.sender?.name || 'Unknown Sender'}
                            </RowTitle>
                            <RowMeta>
                                <span style={{ fontStyle: 'italic', color: '#566071' }}>
                                    &quot;{r.maskedContent || '—'}&quot;
                                </span>
                            </RowMeta>
                            <RowMeta dim>
                                {Array.isArray(r.reasons) ? r.reasons.join(', ') : '—'} · Mode: {r.mode || '—'}
                                {r.request && <> · Request: <a href={`#/requests/${r.request}/show`} style={{ color: '#7c3aed', textDecoration: 'none', fontSize: 12 }}>{String(r.request).slice(-8)}</a></>}
                                {' · '}{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}
                            </RowMeta>

                            {/* Resolution controls for PENDING items */}
                            {r.status === 'PENDING' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10, maxWidth: 420 }}>
                                    <select
                                        value={resolutions[r._id]?.resolution || ''}
                                        onChange={e => setResolutions(prev => ({
                                            ...prev,
                                            [r._id]: { ...prev[r._id], resolution: e.target.value }
                                        }))}
                                        style={{
                                            width: '100%',
                                            minWidth: 0,
                                            padding: '7px 10px',
                                            borderRadius: 8,
                                            border: '2px solid #a855f7',
                                            fontSize: 13,
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <option value="">Select resolution…</option>
                                        <option value="CONFIRMED_PHONE">Confirmed Phone</option>
                                        <option value="FALSE_POSITIVE">False Positive</option>
                                    </select>
                                    <input
                                        type="text"
                                        placeholder="Audit note (optional, max 500 chars)…"
                                        value={resolutions[r._id]?.note || ''}
                                        maxLength={NOTE_MAX_LENGTH}
                                        onChange={e => setResolutions(prev => ({
                                            ...prev,
                                            [r._id]: { ...prev[r._id], note: e.target.value }
                                        }))}
                                        style={{
                                            width: '100%',
                                            minWidth: 0,
                                            padding: '7px 10px',
                                            borderRadius: 8,
                                            border: '2px solid #a855f7',
                                            fontSize: 13,
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                        }}
                                    />
                                    {/* Per-row error */}
                                    {resolveErrors[r._id] && (
                                        <div role="alert" style={{ color: '#ef4444', fontSize: 12, fontWeight: 600 }}>
                                            {resolveErrors[r._id]}
                                        </div>
                                    )}
                                    {/* Character count hint when note is long */}
                                    {(resolutions[r._id]?.note || '').length > NOTE_MAX_LENGTH - 50 && (
                                        <div style={{ fontSize: 11, color: '#7f8999', textAlign: 'right' }}>
                                            {(resolutions[r._id]?.note || '').length}/{NOTE_MAX_LENGTH}
                                        </div>
                                    )}
                                    <button
                                        disabled={!resolutions[r._id]?.resolution || resolving[r._id]}
                                        onClick={() => handleResolve(r._id)}
                                        style={{
                                            padding: '8px 16px',
                                            borderRadius: 8,
                                            border: 'none',
                                            background: resolutions[r._id]?.resolution ? '#7c3aed' : '#e7eaf0',
                                            color: resolutions[r._id]?.resolution ? '#fff' : '#566071',
                                            fontWeight: 700,
                                            fontSize: 13,
                                            cursor: resolutions[r._id]?.resolution ? 'pointer' : 'default',
                                            width: '100%',
                                        }}
                                    >
                                        {resolving[r._id] ? 'Resolving…' : 'Resolve'}
                                    </button>
                                </div>
                            )}

                            {/* Show resolution for already-resolved items */}
                            {r.status !== 'PENDING' && r.resolvedBy && (
                                <RowMeta dim>
                                    Resolved by {r.resolvedBy.name || '—'}
                                    {r.resolvedAt && ` · ${new Date(r.resolvedAt).toLocaleDateString()}`}
                                    {r.resolutionNote && ` · Note: ${r.resolutionNote}`}
                                </RowMeta>
                            )}
                        </RowText>
                    </Row>
                ))}

                {/* Pagination */}
                {!loading && !error && (reviews.length > 0 || page > 1) && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 12, padding: '16px 0' }}>
                        <button
                            disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e7eaf0', background: '#fff', cursor: page > 1 ? 'pointer' : 'default', fontSize: 13 }}
                        >
                            ← Previous
                        </button>
                        <span style={{ alignSelf: 'center', fontSize: 13, color: '#566071' }}>Page {page}</span>
                        <button
                            disabled={!hasNextPage}
                            onClick={() => setPage(p => p + 1)}
                            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e7eaf0', background: '#fff', cursor: hasNextPage ? 'pointer' : 'default', fontSize: 13 }}
                        >
                            Next →
                        </button>
                    </div>
                )}
            </Surface>
        </PageShell>
    );
}
